import React, { useEffect, useState } from "react";
import { useMutation } from "urql";
import Logo from "../../menas-logo-min.png";
import { useAppDispatch } from '../../app/hooks';
import { loader } from 'graphql.macro'
import {
  setAuthInfo,
  setIsAuth
} from '../../features/userLogin/userLoginSlice';
import { get, isEmpty } from "lodash";
import { useNavigate, useLocation } from "react-router-dom";
import { FormLabel } from "../../components/Form/FormLabel";
import { FormInput } from "../../components/Form/FormInput";
import { FormattedMessage, useIntl } from "react-intl";
import { FormButton } from "../../components/Form/FormButton";
import { AvoRedApp } from "../../components/Layout/AvoRedApp";
import { setMessage } from "../../features/flash/flashSlice";
import { DebugGraphqlErrorMessage } from "../../components/DebugGraphqlErrorMessage";
import { setUserId } from "../../features/cart/cartSlice";
declare global {
  interface Window { x: any; }
}

export const LoginPage = () => {

  const [debugMessage, setDebugMessage] = useState('');
  const [formValidation, setFormValidation] = useState({});
  const [generalError, setGeneralError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl()

  const CustomerLoginQuery = loader('../../graphql/mutation/auth/Login.graphql')
  const [customerLoginResult, customerLogin] = useMutation(CustomerLoginQuery)
  
  const { fetching } = customerLoginResult;

  const dispatch = useAppDispatch();

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const emailOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
  }
  const passwordOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value)
  }

  useEffect(() => {
      document.title = 'Login into your avored account';
  }, []);

  const submitHandle = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    
    // Reset error states
    setDebugMessage('')
    setFormValidation({})
    setGeneralError('')
    
    const variables = { email, password }
    customerLogin(variables).then((mutationResult) => {
      
      // Handle GraphQL errors
      if (mutationResult.error) {
        // Check for debug message
        const debugMsg = get(mutationResult, 'error.graphQLErrors[0].originalError.debugMessage', '')
        if (!isEmpty(debugMsg)) {
          setDebugMessage(debugMsg)
        }

        // Check for validation errors
        const validation = get(mutationResult, 'error.graphQLErrors.0.extensions.validation', {})
        if (!isEmpty(validation)) {
          setFormValidation(validation)
        }

        // Check for general GraphQL error message
        const graphQLError = get(mutationResult, 'error.graphQLErrors[0].message', '')
        const networkError = get(mutationResult, 'error.networkError.message', '')
        
        if (!isEmpty(graphQLError) && isEmpty(debugMsg) && isEmpty(validation)) {
          setGeneralError(graphQLError)
        } else if (!isEmpty(networkError)) {
          setGeneralError(networkError)
        } else if (mutationResult.error.message) {
          setGeneralError(mutationResult.error.message)
        }
        
        return
      }

      // Handle successful login
      const authInfo = get(mutationResult, 'data.login')
      if (!isEmpty(authInfo)) {
        dispatch(setAuthInfo(authInfo))
        dispatch(setUserId(authInfo.id))
        dispatch(setIsAuth(true))
        dispatch(setMessage('Bạn đã đăng nhập thành công!'))
        // If we have a pending add-to-cart action in localStorage, perform it now
        try {
          const pending = localStorage.getItem('pendingAddToCart')
          if (pending) {
            JSON.parse(pending)
            localStorage.removeItem('pendingAddToCart')
            // Decide where to redirect back
            const fromPath = (location.state as any)?.from || '/'
            navigate(fromPath)
          } else {
            navigate('/user/profile')
          }
        } catch {
          navigate('/user/profile')
        }
      }
    }).catch((error) => {
      // Handle unexpected errors
      setGeneralError(error.message || 'An unexpected error occurred. Please try again.')
    });
  }
  return (
    <AvoRedApp>
        <main>
          <div className="flex justify-center mt-5">
            <div className="w-full shadow-md py-12 px-4 sm:px-6 lg:px-8  max-w-md mt-5 space-y-8">
                <img
                  className="mx-auto h-12 w-auto"
                  src={Logo}
                  alt={intl.formatMessage({id: "avored_ecommerce_title"})}
                />
                <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
                    <FormattedMessage id="sign_into_your_account" />
                </h2>

                <form className="mt-8 space-y-6" onSubmit={(e) => submitHandle(e)} action="#" method="POST">
                  
                  <DebugGraphqlErrorMessage message={debugMessage} />
                  
                  {generalError && (
                    <div className="bg-red-100 text-sm py-3 px-5 rounded text-red-700">
                      {generalError}
                    </div>
                  )}

                  <div className="space-y-1 rounded-md">
                    <FormLabel 
                        forId="email-address"
                        labelText={intl.formatMessage({id: "email_address"})}
                      />
                    <FormInput 
                        autofocus={true} 
                        id="email-address" 
                        value={email} 
                        errorMessages={get(formValidation, 'email', [])}
                        type="text" 
                        setOnChange={emailOnChange} 
                        placeholder={intl.formatMessage({id: "email_address"})}
                      />
                  </div>
                  
                  <div className="space-y-1 rounded-md shadow-sm">
                    <FormLabel
                        forId="password"
                        labelText={intl.formatMessage({id: "password"})}
                      />
                    <FormInput
                        id="password"
                        type="password"
                        value={password}
                        setOnChange={passwordOnChange}
                        errorMessages={get(formValidation, 'password', [])}
                        placeholder={intl.formatMessage({id: "password"})}
                      />

                  </div>

                  <div>
                      <FormButton type="submit" disabled={fetching}>
                        <div className="relative w-full flex items-center justify-center">
                          {fetching ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              <FormattedMessage id="signing_in" defaultMessage="Signing in..." />
                            </>
                          ) : (
                            <>
                              
                              <FormattedMessage id="sign_in" />
                            </>
                          )}
                        </div>
                      </FormButton>
                  </div>
                </form>
            </div>
          </div>
        </main>
    </AvoRedApp>
  );
};
